import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FileText, Search, ChevronDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";

const INITIAL_VISIBLE = 3;

type CannedResponse = {
  id: string;
  title: string;
  body: string;
  cc?: string | null;
  bcc?: string | null;
  subject?: string | null;
  inboxId?: string | null;
  contentSid?: string | null;
};

type ContactData = {
  name?: string;
  phoneNumber?: string;
  email?: string | null;
  company?: string | null;
  jobTitle?: string | null;
};

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function replacePlaceholders(
  body: string,
  contact?: ContactData | null,
  customFieldValues?: Record<string, string>,
  agentName?: string | null,
): string {
  const c = contact || {};
  const nameParts = (c.name || "").split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  const fieldOrder = [firstName, lastName, c.phoneNumber || "", c.email || "", c.company || "", c.jobTitle || ""];

  const standardVars: Record<string, string> = {
    first_name: firstName,
    last_name: lastName,
    phone: c.phoneNumber || "",
    email: c.email || "",
    company: c.company || "",
    job_title: c.jobTitle || "",
    agent_name: agentName || "",
  };

  const allVars: Record<string, string> = { ...standardVars, ...(customFieldValues || {}) };

  return body
    .replace(/\{\{(\w+)\}\}/g, (match, key) => (key in allVars ? allVars[key] : match))
    .replace(/\{\{(\d+)\}\}/g, (_, num) => {
      const idx = parseInt(num, 10) - 1;
      return fieldOrder[idx] || "";
    });
}

function buildContentVariables(
  body: string,
  contact?: ContactData | null,
  agentName?: string | null,
): Record<string, string> | null {
  const c = contact || {};
  const nameParts = (c.name || "").split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  const namedPlaceholderMap: Record<string, string> = {
    first_name: firstName,
    last_name: lastName,
    phone: c.phoneNumber || "",
    email: c.email || "",
    company: c.company || "",
    job_title: c.jobTitle || "",
    agent_name: agentName || "",
  };

  const fieldOrder = [firstName, lastName, c.phoneNumber || "", c.email || "", c.company || "", c.jobTitle || ""];

  const varMap: Record<string, string> = {};

  const namedMatches = body.match(/\{\{[a-z_]+\}\}/g);
  if (namedMatches) {
    let idx = 1;
    const seen = new Set<string>();
    namedMatches.forEach((m: string) => {
      const key = m.replace(/[{}]/g, "");
      if (!seen.has(key)) {
        seen.add(key);
        varMap[String(idx)] = namedPlaceholderMap[key] || "";
        idx++;
      }
    });
  }

  const numberedMatches = body.match(/\{\{\d+\}\}/g);
  if (numberedMatches) {
    numberedMatches.forEach((m: string) => {
      const num = m.replace(/[{}]/g, "");
      const idx = parseInt(num, 10) - 1;
      varMap[num] = fieldOrder[idx] || "";
    });
  }

  return Object.keys(varMap).length > 0 ? varMap : null;
}

function TemplateList({
  filtered,
  visible,
  hasMore,
  showAll,
  remainingCount,
  onSelect,
  onShowAll,
}: {
  filtered: CannedResponse[];
  visible: CannedResponse[];
  hasMore: boolean;
  showAll: boolean;
  remainingCount: number;
  onSelect: (r: CannedResponse) => void;
  onShowAll: () => void;
}) {
  return (
    <div className="max-h-60 overflow-y-auto">
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground p-3 text-center" data-testid="text-no-templates">No templates found</p>
      ) : (
        <>
          {visible.map((r) => (
            <button
              key={r.id}
              className="w-full text-left px-3 py-2 hover-elevate cursor-pointer border-b last:border-b-0"
              onClick={() => onSelect(r)}
              data-testid={`button-select-template-${r.id}`}
            >
              <p className="text-sm font-medium truncate">{r.title}</p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {(() => { const plain = stripHtml(r.body); return plain.length > 60 ? plain.slice(0, 60) + "..." : plain; })()}
              </p>
            </button>
          ))}
          {hasMore && !showAll && (
            <button
              className="w-full flex items-center justify-center gap-1 px-3 py-2 text-xs text-muted-foreground hover-elevate cursor-pointer"
              onClick={onShowAll}
              data-testid="button-show-all-templates"
            >
              <ChevronDown className="h-3 w-3" />
              Show {remainingCount} more template{remainingCount !== 1 ? "s" : ""}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function CannedResponseSelector({
  channel,
  contact,
  customFieldValues,
  agentName,
  onSelect,
  insideDialog,
  onContentSidSelect,
  onSelectCcBcc,
  onSelectSubjectInbox,
}: {
  channel: "sms" | "ticket" | "whatsapp" | "livechat";
  contact?: ContactData | null;
  customFieldValues?: Record<string, string>;
  agentName?: string | null;
  onSelect: (text: string) => void;
  insideDialog?: boolean;
  onContentSidSelect?: (contentSid: string | null, contentVariables: Record<string, string> | null) => void;
  onSelectCcBcc?: (cc: string, bcc: string) => void;
  onSelectSubjectInbox?: (subject: string, inboxId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isWhatsApp = channel === "whatsapp";

  const { data: waApprovedTemplates } = useQuery<CannedResponse[]>({
    queryKey: ["/api/whatsapp/templates"],
    queryFn: async () => {
      const res = await fetch("/api/whatsapp/templates", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      const templates = await res.json();
      return templates
        .filter((t: any) => t.isActive !== false)
        .map((t: any) => ({ id: t.id, title: t.name, body: t.body, contentSid: t.contentSid }));
    },
    enabled: isWhatsApp,
  });

  const { data: cannedTemplates } = useQuery<CannedResponse[]>({
    queryKey: ["/api/canned-responses", channel],
    queryFn: async () => {
      const res = await fetch(`/api/canned-responses?channel=${channel}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const responses = isWhatsApp
    ? [...(waApprovedTemplates || []), ...(cannedTemplates || [])]
    : (cannedTemplates || []);

  if (!responses || responses.length === 0) return null;

  const filtered = (responses || []).filter(r =>
    (r.title || "").toLowerCase().includes(search.toLowerCase()) ||
    stripHtml(r.body || "").toLowerCase().includes(search.toLowerCase())
  );
  const isSearching = search.length > 0;
  const hasMore = !isSearching && filtered.length > INITIAL_VISIBLE;
  const visible = isSearching || showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const remainingCount = filtered.length - INITIAL_VISIBLE;

  const handleSelect = (r: CannedResponse) => {
    const text = replacePlaceholders(r.body, contact, customFieldValues, agentName);
    onSelect(text);
    if (isWhatsApp && onContentSidSelect) {
      if (r.contentSid) {
        onContentSidSelect(r.contentSid, buildContentVariables(r.body, contact, agentName));
      } else {
        onContentSidSelect(null, null);
      }
    }
    if (onSelectCcBcc) {
      onSelectCcBcc(r.cc || "", r.bcc || "");
    }
    if (onSelectSubjectInbox) {
      onSelectSubjectInbox(r.subject || "", r.inboxId || "");
    }
    setOpen(false);
    setSearch("");
    setShowAll(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearch("");
      setShowAll(false);
    }
  };

  if (insideDialog) {
    return (
      <div className="relative">
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleOpenChange(!open)}
          data-testid="button-canned-responses"
        >
          <FileText className="h-3.5 w-3.5 mr-1" />
          Select Templates
        </Button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-72 rounded-md border bg-popover text-popover-foreground shadow-lg" style={{ zIndex: 100 }} data-testid="panel-template-selector">
            <div className="flex items-center justify-between gap-2 p-2 border-b">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 h-8 text-sm"
                  data-testid="input-search-templates"
                />
              </div>
              <button
                onClick={() => handleOpenChange(false)}
                className="p-1 rounded-md hover-elevate text-muted-foreground"
                data-testid="button-close-templates"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <TemplateList
              filtered={filtered}
              visible={visible}
              hasMore={hasMore}
              showAll={showAll}
              remainingCount={remainingCount}
              onSelect={handleSelect}
              onShowAll={() => setShowAll(true)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid="button-canned-responses">
          <FileText className="h-3.5 w-3.5 mr-1" />
          Select Templates
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid="input-search-templates"
            />
          </div>
        </div>
        <TemplateList
          filtered={filtered}
          visible={visible}
          hasMore={hasMore}
          showAll={showAll}
          remainingCount={remainingCount}
          onSelect={handleSelect}
          onShowAll={() => setShowAll(true)}
        />
      </PopoverContent>
    </Popover>
  );
}
